/**
 * Help content for EyeconX Clinic — FAQs and support options.
 */

export const PATIENT_FILES_GROUP_ID = 'patient-files';

export const FAQ_CATEGORY_GROUPS = [
    {
        id: PATIENT_FILES_GROUP_ID,
        title: 'Patient Files',
        icon: 'bi-folder2-open',
        theme: 'indigo',
    },
];

export const FAQ_CATEGORIES = [
    {
        id: 'patient-files-directory',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Directory & Search',
        icon: 'bi-folder2-open',
        theme: 'indigo',
    },
    {
        id: 'patient-profile',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Patient Info',
        icon: 'bi-person-vcard',
        theme: 'indigo',
    },
    {
        id: 'patient-appointments',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Appointments',
        icon: 'bi-calendar-event',
        theme: 'purple',
    },
    {
        id: 'patient-communication',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Communication',
        icon: 'bi-chat-left-text',
        theme: 'success',
    },
    {
        id: 'patient-activity',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Activity',
        icon: 'bi-clock-history',
        theme: 'violet',
    },
    {
        id: 'patient-invalid-info',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Invalid Info',
        icon: 'bi-exclamation-octagon',
        theme: 'danger',
    },
    {
        id: 'my-files',
        title: 'My Files',
        icon: 'bi-folder',
        theme: 'slate',
    },
    {
        id: 'pms-update-history',
        title: 'PMS Update History',
        icon: 'bi-arrow-repeat',
        theme: 'cyan',
    },
    {
        id: 'general',
        title: 'General',
        icon: 'bi-life-preserver',
        theme: 'primary',
    },
];

export const FAQ_ITEMS = [
    {
        id: 'patient-files-overview',
        categoryId: 'patient-files-directory',
        question: 'What is Patient Files?',
        answer:
            'Patient Files is your clinic directory for searching and opening active patient records. From the list you can sort and paginate results, copy email addresses, and open a patient\'s full profile with appointments, communication preferences, activity history, and invalid contact logs.',
    },
    {
        id: 'patient-files-access',
        categoryId: 'patient-files-directory',
        question: 'How do I open Patient Files?',
        answer:
            'In the left sidebar, click Patient Files. The page opens in search mode—run a search or click Show All Patients to load your clinic\'s active patient directory.',
    },
    {
        id: 'patient-files-search',
        categoryId: 'patient-files-directory',
        question: 'How do I search for a patient?',
        answer:
            'Choose a Search By field: First Name, Last Name, Email, Preferred Name, Home Phone, Cell Phone, or PMS ID. Enter your term and click Search. Partial matches are supported—for example, searching first name "Ann" can return "Anna" and "Joann". PMS ID uses an exact match.',
    },
    {
        id: 'patient-files-show-all',
        categoryId: 'patient-files-directory',
        question: 'What does Show All Patients do?',
        answer:
            'Show All loads every active patient in your clinic without a search filter. Inactive patients are not included in this directory. Use the toolbar to change sort order, direction, or how many rows appear per page.',
    },
    {
        id: 'patient-files-sort',
        categoryId: 'patient-files-directory',
        question: 'How do I sort the patient list?',
        answer:
            'Use the Sort dropdowns in the toolbar to pick a field (First Name, Last Name, Email, Preferred Name, Last Updated, Created Date, or PMS ID) and Asc/Desc order. You can also click sortable column headers in the table—First Name, Last Name, Preferred Name, and Email—to toggle sort direction.',
    },
    {
        id: 'patient-files-pagination',
        categoryId: 'patient-files-directory',
        question: 'How does pagination work?',
        answer:
            'After a search or Show All, the toolbar shows total patients and the current page. Change Show entries to 10, 20, or 50 rows per page. Use the pagination controls below the table to move between pages without losing your current search or sort.',
    },
    {
        id: 'patient-files-open',
        categoryId: 'patient-files-directory',
        question: 'How do I open a patient record?',
        answer:
            'Click anywhere on a patient row or use the View button. You are taken to that patient\'s profile at /patient-files/{patientId}. You can also open a patient from Appointments or other pages that link to Patient Files.',
    },
    {
        id: 'patient-files-copy-email',
        categoryId: 'patient-files-directory',
        question: 'Can I copy a patient email from the list?',
        answer:
            'Yes. In the Email column, click the copy icon next to an address. The icon changes to a check mark when the email is on your clipboard.',
    },
    {
        id: 'patient-files-reset',
        categoryId: 'patient-files-directory',
        question: 'How do I clear my search and start over?',
        answer:
            'Click Reset in the toolbar to clear the loaded directory and return to the initial search screen. You can also clear the search box with the X button and run a new Search.',
    },
    {
        id: 'patient-files-empty',
        categoryId: 'patient-files-directory',
        question: 'Why is the list empty?',
        answer:
            'On first visit, Patient Files waits for you to search or click Show All Patients. If you searched and see no results, try another field or a shorter partial term. Confirm the patient is active—inactive patients do not appear in the directory.',
    },
    {
        id: 'patient-files-status',
        categoryId: 'patient-files-directory',
        question: 'What do Active and Welcome Sent mean in the list?',
        answer:
            'Active means the patient record is currently active in EyeconX. Inactive patients are hidden from the directory but can still be opened by direct link if you have their ID. Welcome Sent indicates a welcome communication has already been sent to that patient.',
    },
    {
        id: 'patient-profile-overview',
        categoryId: 'patient-profile',
        question: 'What is on the patient profile page?',
        answer:
            'The profile page shows a summary header with name, PMS ID, date of birth, gender, active status, welcome status, contact cards, address, and quick-action shortcuts. Below that, tabs let you manage Patient Info, Appointments, Communication, Activity, and Invalid Info for that patient.',
    },
    {
        id: 'patient-profile-back',
        categoryId: 'patient-profile',
        question: 'How do I return to the patient list?',
        answer:
            'Click Back to Patient Files in the page header. Your previous search or Show All state is preserved in the app until you click Reset on the list page.',
    },
    {
        id: 'patient-profile-summary',
        categoryId: 'patient-profile',
        question: 'What can I do in the patient summary header?',
        answer:
            'Review the patient\'s identity, PMS ID, age, gender, and status pills. Copy email, cell, or home phone using the copy buttons on each contact card. The address section shows the patient\'s mailing address when available.',
    },
    {
        id: 'patient-profile-quick-actions',
        categoryId: 'patient-profile',
        question: 'Why are quick action buttons disabled?',
        answer:
            'Quick actions such as Send Email, Send SMS, Send Survey, and Send Articles require the right contact detail on file and the matching communication preference to be enabled. Welcome is also disabled after a welcome message has already been sent. Hover a disabled button to see the reason.',
    },
    {
        id: 'patient-profile-info-tab',
        categoryId: 'patient-profile',
        question: 'What can I edit on the Patient Info tab?',
        answer:
            'Click Edit to update email, cell number, home phone, and the Active patient toggle. First name, last name, birth date, age, and gender are read-only—they sync from your practice management system. Click Save to apply changes or Cancel to discard them.',
    },
    {
        id: 'patient-profile-active',
        categoryId: 'patient-profile',
        question: 'What happens when I mark a patient inactive?',
        answer:
            'Inactive patients no longer appear in the Patient Files directory or Show All list. Their record still exists and can be reopened by direct link. Turn Active back on from the Patient Info tab if the patient should return to the directory.',
    },
    {
        id: 'patient-profile-not-found',
        categoryId: 'patient-profile',
        question: 'Why does the profile say Patient not found?',
        answer:
            'The patient ID in the URL may be invalid, the record may have been removed, or your clinic may not have access to that patient. Use Return to Patient Files and search again, or confirm you have the correct link.',
    },
    {
        id: 'patient-appointments-overview',
        categoryId: 'patient-appointments',
        question: 'What does the Appointments tab show?',
        answer:
            'The Appointments tab lists upcoming and past appointments for the current patient. Each row includes date and time, doctor, duration, confirmation status, and reminder delivery icons for enabled reminder types (Prebooked, First, Second, Last, Same Day).',
    },
    {
        id: 'patient-appointments-today',
        categoryId: 'patient-appointments',
        question: 'What does the Today badge mean?',
        answer:
            'A yellow Today badge appears next to appointments scheduled for the current calendar day, making it easy to spot same-day visits in the patient\'s history.',
    },
    {
        id: 'patient-appointments-status',
        categoryId: 'patient-appointments',
        question: 'What do appointment status badges mean?',
        answer:
            'Not Confirmed means the appointment has not been confirmed in EyeconX. Confirmed by Eyeconx and Confirmed by PMS indicate confirmation through EyeconX or your practice management system. Cancelled means the appointment was marked cancelled.',
    },
    {
        id: 'patient-appointments-reminders',
        categoryId: 'patient-appointments',
        question: 'How do I read reminder icons?',
        answer:
            'Colored icons show which reminder types are enabled for that appointment and whether a reminder was sent. Hover an icon for details such as the reminder label, send date, and channel (Email, SMS, Call, or Live Call). Icons only appear for reminder types that are active on the appointment.',
    },
    {
        id: 'patient-appointments-pagination',
        categoryId: 'patient-appointments',
        question: 'Why do I only see some appointments?',
        answer:
            'Appointments load in pages of 10. Use the pagination controls at the bottom to view older or additional records. For the full clinic schedule on a specific day, open Appointments from the main sidebar.',
    },
    {
        id: 'patient-appointments-clinic-page',
        categoryId: 'patient-appointments',
        question: 'How is this different from the main Appointments page?',
        answer:
            'The patient Appointments tab shows history for one patient only. The main Appointments page shows all clinic appointments for a selected date, with ECP filtering, status filters, print, and confirm/cancel actions for that day\'s schedule.',
    },
    {
        id: 'patient-communication-overview',
        categoryId: 'patient-communication',
        question: 'What are communication preferences?',
        answer:
            'Communication preferences control which channels and notification types EyeconX may use for a patient. Channels include Email, SMS, and Phone Call, each with a priority (Primary, Secondary, or Tertiary). Notifications cover Reminders, Newsletter, Articles, Recall, Glasses, Lenses, Lens Orders, Survey, Thank You, Follow-up, and Birthday.',
    },
    {
        id: 'patient-communication-channels',
        categoryId: 'patient-communication',
        question: 'How do I edit communication channels?',
        answer:
            'On the Communication tab, click Edit in the Communication Channels section. Toggle each channel on or off and set its priority. Use Enable All or Disable All for quick changes. Save applies only that section; Cancel restores the last saved values.',
    },
    {
        id: 'patient-communication-notifications',
        categoryId: 'patient-communication',
        question: 'How do I edit notification types?',
        answer:
            'Click Edit in the Notifications & Content section. Toggle individual notification types on or off, or use Enable All / Disable All. Save to store your changes. Only one section can be edited at a time—finish or cancel before editing the other.',
    },
    {
        id: 'patient-communication-effect',
        categoryId: 'patient-communication',
        question: 'How do preferences affect outreach?',
        answer:
            'Disabled channels or notification types can block automated sends and disable related quick actions on the patient summary. For example, if SMS is disabled, Send SMS quick action is unavailable. Update contact details on the Patient Info tab if a channel is enabled but contact information is missing.',
    },
    {
        id: 'patient-communication-priority',
        categoryId: 'patient-communication',
        question: 'What is channel priority for?',
        answer:
            'Primary, Secondary, and Tertiary priority ranks Email, SMS, and Phone Call when EyeconX chooses how to reach a patient. Set the patient\'s preferred channel as Primary and backup channels as Secondary or Tertiary.',
    },
    {
        id: 'patient-activity-overview',
        categoryId: 'patient-activity',
        question: 'What does the Activity tab show?',
        answer:
            'Activity lists communication and system events for the patient—such as emails, SMS, calls, and other logged interactions. Each entry shows a summary in the list; open View Details for the full activity record in a modal.',
    },
    {
        id: 'patient-activity-details',
        categoryId: 'patient-activity',
        question: 'How do I view activity details?',
        answer:
            'Click an activity row or its View Details control. A modal opens with expanded information about that event. Close the modal to return to the list.',
    },
    {
        id: 'patient-activity-pagination',
        categoryId: 'patient-activity',
        question: 'How much activity history is shown?',
        answer:
            'Activity loads 20 records per page. Use pagination at the bottom to browse older events. For clinic-wide activity, open Activity from the Misc section in the sidebar.',
    },
    {
        id: 'patient-activity-empty',
        categoryId: 'patient-activity',
        question: 'Why is there no activity for this patient?',
        answer:
            'No logged events exist yet for that patient, or activity has not synced. New communications and system actions will appear here as they are recorded.',
    },
    {
        id: 'patient-invalid-overview',
        categoryId: 'patient-invalid-info',
        question: 'What is the Invalid Info tab?',
        answer:
            'Invalid Info shows contact problems logged for this patient—such as bounced emails or invalid phone numbers detected during delivery or validation. Use it to review bad data tied to a specific patient without leaving their profile.',
    },
    {
        id: 'patient-invalid-types',
        categoryId: 'patient-invalid-info',
        question: 'What types of invalid details are tracked?',
        answer:
            'Invalid Email (red), Invalid Cell Phone (yellow), and Invalid Home Phone (gray). Each row shows the invalid value, patient name, and the date it was logged.',
    },
    {
        id: 'patient-invalid-delete',
        categoryId: 'patient-invalid-info',
        question: 'How do I remove invalid detail records?',
        answer:
            'Delete a single row with the trash icon on that row, or select multiple checkboxes and click Delete Selected. Confirm in the dialog—removed records no longer appear in this patient\'s invalid log. Fix the underlying contact on the Patient Info tab so future sends use valid details.',
    },
    {
        id: 'patient-invalid-empty',
        categoryId: 'patient-invalid-info',
        question: 'What does "No invalid details found" mean?',
        answer:
            'No invalid contact records are currently logged for this patient. That is expected when email and phone details are valid or have not failed validation.',
    },
    {
        id: 'patient-invalid-clinic-page',
        categoryId: 'patient-invalid-info',
        question: 'How is this related to Invalid Details in the sidebar?',
        answer:
            'The Invalid Info tab is per patient. Invalid Details under Misc in the sidebar is a clinic-wide view where you can search, filter, sort, and print all invalid records across patients. Deleting from either place removes that log entry.',
    },
    {
        id: 'my-files-overview',
        categoryId: 'my-files',
        question: 'What is My Files?',
        answer:
            'My Files is your clinic\'s media library in EyeconX. Upload and manage images, PDFs, and other supported assets in one place. Each file gets a hosted URL you can copy and reuse across EyeconX—for example in articles, newsletters, or other content that needs a stable link to clinic media.',
    },
    {
        id: 'my-files-access',
        categoryId: 'my-files',
        question: 'How do I open My Files?',
        answer:
            'In the left sidebar, open the Misc section and click My Files. The page header shows your total file count and includes a Refresh button to reload the library after uploads or deletions.',
    },
    {
        id: 'my-files-upload-button',
        categoryId: 'my-files',
        question: 'How do I upload a file?',
        answer:
            'Click Upload Media in the toolbar (or Upload on smaller screens). In the upload window, drag a file onto the drop zone or click to browse your device. You can also drag a file anywhere on the My Files page—a drop overlay appears and the upload starts when you release the file. Only one file is uploaded at a time.',
    },
    {
        id: 'my-files-formats',
        categoryId: 'my-files',
        question: 'Which file types are supported?',
        answer:
            'Supported uploads include common image formats (JPEG, PNG, GIF, BMP, TIFF, ICO, HEIF, HEIC, and similar image types) and PDF documents. If a file type is not supported, the upload fails with an error—convert the file to a supported format and try again.',
    },
    {
        id: 'my-files-upload-progress',
        categoryId: 'my-files',
        question: 'What happens while a file is uploading?',
        answer:
            'The upload modal shows a progress bar and percentage while the file is sent to EyeconX. The modal cannot be closed until the upload finishes. When complete, the modal closes automatically and your library refreshes so the new file appears in the grid.',
    },
    {
        id: 'my-files-search',
        categoryId: 'my-files',
        question: 'How do I search my library?',
        answer:
            'Use the Search files field in the toolbar. Type part of a file name and press Enter or click Search. Matching files are shown and the file count chip updates. Click Clear to remove the filter and show the full library again.',
    },
    {
        id: 'my-files-sort',
        categoryId: 'my-files',
        question: 'How can I sort files?',
        answer:
            'Open the sort dropdown in the toolbar. Choose Newest First (default), Oldest First, Name A–Z, or Name Z–A. Sorting applies to the full library and works together with search—changing sort or search resets pagination to the first page.',
    },
    {
        id: 'my-files-pagination',
        categoryId: 'my-files',
        question: 'Why do I only see some files on the page?',
        answer:
            'My Files shows files in a responsive grid (two to four columns depending on screen width) with three rows per page. Use the pagination controls at the bottom to move between pages. Resizing the browser may change how many files fit on each page.',
    },
    {
        id: 'my-files-preview',
        categoryId: 'my-files',
        question: 'Can I preview a file before using it?',
        answer:
            'Yes, for image files. Click the thumbnail on a file card to open a larger preview. From the preview you can open the image in a new browser tab. PDFs and other non-image files show a document icon instead of a preview—use Copy URL or open the link directly if you need to view them.',
    },
    {
        id: 'my-files-copy-url',
        categoryId: 'my-files',
        question: 'How do I copy a file URL?',
        answer:
            'Each file card shows the full hosted URL in a read-only field. Click the copy button next to the URL—the icon changes to a check mark when the link is copied to your clipboard. Paste that URL wherever EyeconX or your content needs a direct link to the file.',
    },
    {
        id: 'my-files-delete',
        categoryId: 'my-files',
        question: 'How do I delete a file?',
        answer:
            'Click the trash icon on the file card. Confirm in the dialog—deletion is permanent and removes the file from your clinic media library. Any content that still references the old URL may show a broken link, so update those references before deleting if the file is in use.',
    },
    {
        id: 'my-files-refresh',
        categoryId: 'my-files',
        question: 'When should I use Refresh?',
        answer:
            'Click Refresh in the page header if the library looks out of date—for example after another team member uploaded or deleted files, or if a file did not appear immediately after upload. Refresh reloads the full list from the server without leaving the page.',
    },
    {
        id: 'my-files-empty',
        categoryId: 'my-files',
        question: 'Why does my library look empty?',
        answer:
            'If no files have been uploaded yet, My Files shows an empty state with an Upload Media button. If you applied a search filter, clear it to see all files again. If you expected files from an older workflow, confirm you are signed in to the correct clinic.',
    },
    {
        id: 'my-files-upload-failed',
        categoryId: 'my-files',
        question: 'Why did my upload fail?',
        answer:
            'Common causes include an unsupported file type, a network interruption, or a temporary server error. Check that the file is a supported image or PDF format and try again. If the problem continues, use Refresh, try a smaller file, or contact EyeconX support with the file type and approximate size.',
    },
    {
        id: 'my-files-metadata',
        categoryId: 'my-files',
        question: 'What information is shown on each file card?',
        answer:
            'Each card shows a thumbnail (or a document icon for non-images), the original file name, last modified date, file size, file type badge, the hosted URL, and actions to copy the link or delete the file.',
    },
    {
        id: 'pms-history-overview',
        categoryId: 'pms-update-history',
        question: 'What is PMS Update History?',
        answer:
            'PMS Update History shows a log of sync runs between your Practice Management System (PMS) and EyeconX. Each entry records when a sync occurred, whether it succeeded or failed, which PMS type was involved, and notes with additional detail. Use it to confirm patient and appointment data is updating and to troubleshoot failed syncs.',
    },
    {
        id: 'pms-history-access',
        categoryId: 'pms-update-history',
        question: 'How do I open PMS Update History?',
        answer:
            'In the left sidebar, open the Misc section and click PMS Update History. The page loads your clinic\'s sync log automatically, with the most recent sync at the top.',
    },
    {
        id: 'pms-history-latest-sync',
        categoryId: 'pms-update-history',
        question: 'What does Latest sync in the header mean?',
        answer:
            'Latest sync shows the timestamp of the most recent sync record on the current first page—typically your clinic\'s last completed PMS update. If no history exists yet, it displays a dash until the first sync is logged.',
    },
    {
        id: 'pms-history-list-item',
        categoryId: 'pms-update-history',
        question: 'What does each sync record show?',
        answer:
            'Each row shows the sync timestamp, a Success or Failed status badge, the PMS type (when available), and a Check logs button. Records are sorted newest first so recent activity appears at the top of the list.',
    },
    {
        id: 'pms-history-status',
        categoryId: 'pms-update-history',
        question: 'What do Success and Failed mean?',
        answer:
            'Success means the PMS sync completed without error. Failed means the sync ended with an error—open Check logs on that row and read the Notes section for the error message. Contact EyeconX support with the sync timestamp and notes if failures continue.',
    },
    {
        id: 'pms-history-check-logs',
        categoryId: 'pms-update-history',
        question: 'How do I view sync log details?',
        answer:
            'Click Check logs on any sync row. The Sync Log Details modal shows the date and time, result, PMS type, Sync ID, Clinic ID, Modified At, and a Notes field with the full sync message. Failed syncs highlight notes in red. Close the modal when finished.',
    },
    {
        id: 'pms-history-pms-type',
        categoryId: 'pms-update-history',
        question: 'What is the PMS type badge?',
        answer:
            'The PMS type identifies which Practice Management System connector ran the sync (for example, your configured PMS integration name). The toolbar may also show PMS types present on the current page as a quick reference.',
    },
    {
        id: 'pms-history-pagination',
        categoryId: 'pms-update-history',
        question: 'How do I browse older sync records?',
        answer:
            'Use the pagination controls at the bottom of the list when more than one page exists. The toolbar shows the current page number and total pages. Change Show entries to 10, 25, or 50 rows per page—changing page size returns you to page 1.',
    },
    {
        id: 'pms-history-empty',
        categoryId: 'pms-update-history',
        question: 'Why is there no sync history?',
        answer:
            'If your clinic has not completed a PMS sync yet, the page shows an empty state: "No PMS sync history yet." Once your PMS integration runs its first update, entries will appear here automatically. Confirm your PMS connector is configured and running if you expected history sooner.',
    },
    {
        id: 'pms-history-failed',
        categoryId: 'pms-update-history',
        question: 'What should I do after a failed sync?',
        answer:
            'Open Check logs on the failed row and review Notes for the specific error. Verify your PMS integration is online and that recent changes exist in the PMS to sync. If the next scheduled sync succeeds, no action may be needed. For repeated failures, email EyeconX support with the sync timestamp, PMS type, and notes from the log.',
    },
    {
        id: 'pms-history-vs-activity',
        categoryId: 'pms-update-history',
        question: 'How is this different from Activity?',
        answer:
            'PMS Update History tracks automated sync jobs between your PMS and EyeconX—success/failure and technical notes per sync run. Activity logs patient-facing communications and other clinic events (emails, SMS, calls, etc.). Use PMS Update History for data sync health; use Activity for outreach history.',
    },
    {
        id: 'general-help',
        categoryId: 'general',
        question: 'Where can I find Help & Support?',
        answer:
            'Open Help & Support from the left sidebar (above Logout) or from the Support section in your user panel. Browse FAQs by category, search by keyword, or switch to Get Support for email contact and quick links to common pages.',
    },
    {
        id: 'general-password',
        categoryId: 'general',
        question: 'How do I change my password?',
        answer:
            'Open your user panel from the top navigation and choose Password Settings, or use the Change Password shortcut under Get Support on this page. Enter your current password and a new password that meets the requirements. You may be signed out after a successful change.',
    },
];

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@eyeconx.com';

export const HELP_THEME_BUTTON_COLORS = {
    primary: 'primary',
    indigo: 'primary',
    purple: 'primary',
    cyan: 'info',
    success: 'success',
    slate: 'secondary',
    violet: 'primary',
    danger: 'danger',
    warning: 'warning',
};

export const SUPPORT_OPTIONS = [
    {
        id: 'email',
        title: 'Email Support',
        description: 'Send questions about Patient Files, PMS sync history, or other EyeconX features.',
        icon: 'bi-envelope',
        theme: 'pink',
        color: 'primary',
        actionLabel: 'Email support',
        href: `mailto:${SUPPORT_EMAIL}?subject=EyeconX%20Support`,
        external: true,
    },
    {
        id: 'patient-files',
        title: 'Patient Files',
        description: 'Search active patients, open profiles, and manage contact and communication settings.',
        icon: 'bi-folder2-open',
        theme: 'indigo',
        color: 'primary',
        actionLabel: 'Go to Patient Files',
        to: '/patient-files',
    },
    {
        id: 'invalid-details',
        title: 'Invalid Details',
        description: 'Review clinic-wide invalid email and phone logs across all patients.',
        icon: 'bi-exclamation-octagon',
        theme: 'danger',
        color: 'danger',
        actionLabel: 'Go to Invalid Details',
        to: '/invalid-details',
    },
    {
        id: 'appointments',
        title: 'Appointments',
        description: 'View the clinic schedule by date, filter by ECP, and manage confirmation status.',
        icon: 'bi-calendar-event',
        theme: 'purple',
        color: 'primary',
        actionLabel: 'Go to Appointments',
        to: '/appointments',
    },
    {
        id: 'my-files',
        title: 'My Files',
        description: 'Upload clinic media, copy hosted URLs, preview images, and manage your library.',
        icon: 'bi-folder',
        theme: 'slate',
        color: 'secondary',
        actionLabel: 'Go to My Files',
        to: '/my-files',
    },
    {
        id: 'pms-update-history',
        title: 'PMS Update History',
        description: 'Review PMS sync logs, check success or failure status, and read detailed sync notes.',
        icon: 'bi-arrow-repeat',
        theme: 'cyan',
        color: 'info',
        actionLabel: 'Go to PMS Update History',
        to: '/pms-update-history',
    },
    {
        id: 'password',
        title: 'Change Password',
        description: 'Update your account password from your profile settings.',
        icon: 'bi-shield-lock',
        theme: 'info',
        color: 'secondary',
        actionLabel: 'Change password',
        to: '/update-password',
    },
];

export const getFaqItems = () => FAQ_ITEMS;

export const getSupportOptions = () => SUPPORT_OPTIONS;

export const getCategoryById = (categoryId) =>
    FAQ_CATEGORIES.find((category) => category.id === categoryId) || null;

export const getCategoryGroupById = (groupId) =>
    FAQ_CATEGORY_GROUPS.find((group) => group.id === groupId) || null;

export const getTopLevelCategories = () =>
    FAQ_CATEGORIES.filter((category) => !category.parentId);

export const getChildCategories = (parentId) =>
    FAQ_CATEGORIES.filter((category) => category.parentId === parentId);

export const categoryMatchesFilter = (categoryId, filter) => {
    if (filter === 'all') {
        return true;
    }

    if (categoryId === filter) {
        return true;
    }

    const category = getCategoryById(categoryId);

    return Boolean(category?.parentId === filter);
};

export const buildFaqCategorySections = (faqItems, filter = 'all') => {
    const sections = [];

    FAQ_CATEGORY_GROUPS.forEach((group) => {
        const children = getChildCategories(group.id)
            .map((category) => ({
                ...category,
                items: faqItems.filter(
                    (item) =>
                        item.categoryId === category.id && categoryMatchesFilter(category.id, filter)
                ),
            }))
            .filter((category) => category.items.length > 0);

        if (!children.length) {
            return;
        }

        if (filter !== 'all' && filter !== group.id && !children.some((child) => child.id === filter)) {
            return;
        }

        const visibleChildren =
            filter === group.id
                ? children
                : children.filter((child) => categoryMatchesFilter(child.id, filter));

        if (!visibleChildren.length) {
            return;
        }

        sections.push({
            type: 'group',
            ...group,
            children: visibleChildren,
        });
    });

    getTopLevelCategories().forEach((category) => {
        const items = faqItems.filter(
            (item) =>
                item.categoryId === category.id && categoryMatchesFilter(category.id, filter)
        );

        if (!items.length) {
            return;
        }

        sections.push({
            type: 'category',
            ...category,
            items,
        });
    });

    return sections;
};
